import * as FaIcons from "react-icons/fa";

// Who manages which reservation slots: offline (at the branch) and/or online.
// Mirrors RESERVATION_ACCESS in Server/src/components/reservation/reservation.access.js.
export const RESERVATION_ACCESS = {
  admin: ["offline", "online"],
  asmaa: ["offline", "online"],
  alaa: ["offline", "online"],
  abdelrhman: ["offline"],
  mona: ["online"],
  agent: ["online"],
  Agent: ["online"],
  agent5: ["online"],
};
export const RESERVATION_USERS = Object.keys(RESERVATION_ACCESS);
export const reservationModesFor = (username) => RESERVATION_ACCESS[username] || [];
// Accounts with this role can only use the Scan Reservation page. Mirrors
// SCANNER_ROLE in reservation.routes.js.
export const SCANNER_ROLE = "scanner";
export const SCAN_PATH = "/main/reservations/scan";
export const canScanReservations = (u) => RESERVATION_USERS.includes(u?.username) || u?.role === SCANNER_ROLE;

// Offline = authentication at the branch; online = no branch visit.
export const MODES = [
  { key: "offline", label: "Offline Authentication", labelAr: "التوثيق في المقر", short: "Offline (at the branch)" },
  { key: "online", label: "Online Authentication", labelAr: "التوثيق أونلاين", short: "Online" },
];

// Order, names, fees and colours of the categories. Keys mirror CATEGORIES in
// Server/src/components/reservation/reservation.service.js. Class names are
// written out in full so Tailwind keeps them.
// Note on the Standard + Online cards (slots set by staff availability, mornings only).
const MORNING_NOTES = ["يتم تحديد المواعيد حسب إتاحة موظفي الشركة.", "المواعيد خلال الفترة الصباحية فقط."];
export const CATEGORIES = [
  {
    key: "standard",
    mode: "offline",
    hex: "#0EA5E9", // dot colour on the PDF ticket
    label: "Standard",
    certDays: [50, 60], // days until the certificate is authenticated
    wait: [120, 180], // minutes waited at the branch
    tagline: "خدمة التوثيق العادية",
    extraFee: 0,
    icon: FaIcons.FaCalendarCheck,
    dot: "bg-sky-500",
    iconBox: "bg-sky-100 text-sky-700",
    chip: "bg-sky-50 text-sky-800 ring-sky-200",
    selected: "ring-sky-600 bg-sky-50/60",
  },
  {
    key: "standard_fast",
    mode: "offline",
    hex: "#14B8A6", // dot colour on the PDF ticket
    label: "Standard + Fast",
    certDays: [25, 30], // days until the certificate is authenticated
    wait: [120, 180], // minutes waited at the branch
    tagline: "إجراءات التوثيق في مدة أقصر",
    extraFee: 2000,
    icon: FaIcons.FaBolt,
    dot: "bg-teal-500",
    iconBox: "bg-teal-100 text-teal-700",
    chip: "bg-teal-50 text-teal-800 ring-teal-200",
    selected: "ring-teal-600 bg-teal-50/60",
  },
  {
    key: "vip",
    mode: "offline",
    hex: "#F59E0B", // dot colour on the PDF ticket
    label: "VIP",
    certDays: [50, 60], // days until the certificate is authenticated
    wait: [30, 45], // minutes waited at the branch
    tagline: "اختر موعد التوثيق في الوقت المناسب لك مع فترة انتظار أقل",
    extraFee: 2000,
    icon: FaIcons.FaCrown,
    dot: "bg-amber-500",
    iconBox: "bg-amber-100 text-amber-700",
    chip: "bg-amber-50 text-amber-800 ring-amber-200",
    selected: "ring-amber-600 bg-amber-50/60",
  },
  {
    key: "vip_fast",
    mode: "offline",
    hex: "#8B5CF6", // dot colour on the PDF ticket
    label: "VIP + Fast",
    certDays: [25, 30], // days until the certificate is authenticated
    wait: [30, 45], // minutes waited at the branch
    tagline: "اختر موعد التوثيق في الوقت المناسب لك مع فترة انتظار أقل ومدة أقصر لاستلام الشهادة",
    extraFee: 4000,
    icon: FaIcons.FaRocket,
    dot: "bg-violet-500",
    iconBox: "bg-violet-100 text-violet-700",
    chip: "bg-violet-50 text-violet-800 ring-violet-200",
    selected: "ring-violet-600 bg-violet-50/60",
  },
  {
    key: "standard_online",
    mode: "online",
    hex: "#3B82F6", // dot colour on the PDF ticket
    label: "Standard + Online",
    certDays: [50, 60], // days until the certificate is authenticated
    tagline: "توثيق عبر الإنترنت دون الحاجة لزيارة الفرع",
    notes: MORNING_NOTES, // shown as ملحوظة
    extraFee: 2000,
    icon: FaIcons.FaLaptop,
    dot: "bg-blue-500",
    iconBox: "bg-blue-100 text-blue-700",
    chip: "bg-blue-50 text-blue-800 ring-blue-200",
    selected: "ring-blue-600 bg-blue-50/60",
  },
  {
    key: "standard_online_fast",
    mode: "online",
    hex: "#06B6D4", // dot colour on the PDF ticket
    label: "Standard + Online + Fast",
    certDays: [25, 30], // days until the certificate is authenticated
    tagline: "توثيق أونلاين مع مدة أقصر لاستلام الشهادة",
    notes: MORNING_NOTES, // shown as ملحوظة
    extraFee: 4000,
    foreignFee: 50, // USD: foreigners pay normal fees + this instead of extraFee
    icon: FaIcons.FaFastForward,
    dot: "bg-cyan-500",
    iconBox: "bg-cyan-100 text-cyan-700",
    chip: "bg-cyan-50 text-cyan-800 ring-cyan-200",
    selected: "ring-cyan-600 bg-cyan-50/60",
  },
  {
    key: "vip_online",
    mode: "online",
    hex: "#F97316", // dot colour on the PDF ticket
    label: "VIP + Online",
    certDays: [50, 60], // days until the certificate is authenticated
    tagline: "اختر الموعد المناسب لك في المواعيد المسائية",
    extraFee: 5000,
    foreignFee: 80, // USD: foreigners pay normal fees + this instead of extraFee
    icon: FaIcons.FaGem,
    dot: "bg-orange-500",
    iconBox: "bg-orange-100 text-orange-700",
    chip: "bg-orange-50 text-orange-800 ring-orange-200",
    selected: "ring-orange-600 bg-orange-50/60",
  },
  {
    key: "vip_online_fast",
    mode: "online",
    hex: "#D946EF", // dot colour on the PDF ticket
    label: "VIP + Online + Fast",
    certDays: [25, 30], // days until the certificate is authenticated
    tagline: "اختر الموعد المناسب لك في المواعيد المسائية ومدة أقصر لاستلام الشهادة",
    extraFee: 6000,
    foreignFee: 120, // USD: foreigners pay normal fees + this instead of extraFee
    icon: FaIcons.FaStar,
    dot: "bg-fuchsia-500",
    iconBox: "bg-fuchsia-100 text-fuchsia-700",
    chip: "bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-200",
    selected: "ring-fuchsia-600 bg-fuchsia-50/60",
  },
];
export const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

// Fees: every category pays the normal fees; extraFee (EGP) is added on top.
// Some online categories charge foreign students normal fees + foreignFee (USD)
// instead of the EGP extraFee.
// Coming without a reservation costs WALK_IN_FEE extra.
export const WALK_IN_FEE = 1000;
// Non-breaking spaces keep an Arabic range such as "من 50 إلى 60 يومًا" on one line.
const nowrap = (s) => s.replace(/ /g, "\u00A0");

// Expected waiting time at the branch, e.g. "120–180 min".
export const formatWait = (c) => `${c.wait[0]}–${c.wait[1]} min`;
export const formatWaitAr = (c) => nowrap(`من ${c.wait[0]} إلى ${c.wait[1]} دقيقة`);
// How long the certificate authentication takes, e.g. "50–60 days".
export const formatCertDays = (c) => `${c.certDays[0]}–${c.certDays[1]} days`;
export const CERT_DAYS_LABEL_AR = "الفترة المتوقعة لاستلام الشهادة بعد التوثيق واستلام كافة الأوراق وإتمام الإجراءات";
export const WAIT_LABEL_AR = "مدة الانتظار أثناء إجراءات التوثيق داخل مقر الشركة";
export const formatCertDaysAr = (c) => nowrap(`من ${c.certDays[0]} إلى ${c.certDays[1]} يومًا`);
const egp = (n) => `${n.toLocaleString("en-US")} EGP`;
export const formatFee = (c) => (c.extraFee ? `Normal fees + ${egp(c.extraFee)}` : "Normal fees");
export const formatForeignFee = (c) => `Normal fees + ${c.foreignFee} USD`;
export const formatForeignFeeAr = (c) => `الرسوم العادية + ${c.foreignFee} دولار`;
export const FOREIGN_FEE_LABEL = "Fees for foreigners";
export const FOREIGN_FEE_LABEL_AR = "رسوم الطلاب الأجانب";
// Reservations are for an in-person visit to the branch (nothing is done online).
export const BRANCH_MAP_URL = "https://share.google/ccgdZg4iDCnD1Tq6L";
export const ONLINE_NOTE = "هذا حجز للتوثيق عبر الإنترنت: لا حاجة لزيارة الفرع.";
export const IN_PERSON_NOTE = "هذا حجز حضوري (غير إلكتروني): يرجى الحضور إلى الفرع في موعد حجزك.";
// Papers to bring, by student case — shown on the date & time step.
export const REQUIRED_DOCS = [
  {
    key: "egypt_inside",
    icon: "🎓",
    title: "إجراءات التوثيق للطلاب المصريين (الداخل)",
    heading: "الأوراق المطلوبة للطلاب المصريين:",
    items: [
      ["🆔", "صورة من بطاقة الرقم القومي"],
      ["🛂", "صورة من جواز السفر"],
      ["🏫", "أصل بيان قيد مختوم من المدرسة والإدارة التعليمية التابعة لها، موجَّه إلى وزارة التربية والتعليم"],
    ],
  },
  {
    key: "egypt_abroad",
    icon: "✈️",
    title: "إجراءات التوثيق للطلاب المصريين (الخارج)",
    heading: "الأوراق المطلوبة للطلاب بالخارج:",
    items: [
      ["🏠", "صورة من الإقامة (سارية)"],
      ["🛂", "صورة من جواز السفر"],
      ["📄", "صورة من شهادة ميلاد الطالب وبطاقة الرقم القومي"],
      ["📑", "شهادة تحركات إلكترونية"],
      ["🛬", "صورة من أختام الدخول والخروج"],
    ],
  },
  {
    key: "foreign_inside",
    icon: "🌍",
    title: "إجراءات التوثيق لطلاب الداخل الأجانب",
    heading: "الأوراق المطلوبة لطلاب الداخل الأجانب:",
    items: [
      ["🆔", "صورة من البطاقة"],
      ["🛂", "صورة من جواز السفر"],
      ["🏫", "أصل بيان قيد مختوم من المدرسة والإدارة التعليمية التابعة لها، موجَّه إلى وزارة التربية والتعليم"],
      ["🏠", "صورة من الإقامة المصرية"],
    ],
  },
];
// Same note closes every list above.
export const DOCS_GMAIL_NOTE = "يرجى إحضار إيميل وباسورد الطالب المسجَّل لدى ACT على Gmail. 📧";
export const WALK_IN_NOTE = `الحضور بدون حجز مسبق يستلزم دفع رسوم إضافية قدرها ${WALK_IN_FEE.toLocaleString("en-US")} جنيه.`;

// Lexend (headings) + Source Sans 3 (body) + Noto Kufi Arabic (Arabic, clear at small sizes)
// — loaded only on reservation pages. Cairo stays for the Top Achievers invitation.
export const FONT_HEADING = { fontFamily: "'Lexend', 'Open Sans', sans-serif" };
export const FONT_BODY = { fontFamily: "'Source Sans 3', 'Open Sans', sans-serif" };
// Descriptions on the public form are in Arabic: spread onto the element.
export const AR = { dir: "rtl", lang: "ar", style: { fontFamily: "'Noto Kufi Arabic', 'Segoe UI', Tahoma, sans-serif" } };
export const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Lexend:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=Noto+Kufi+Arabic:wght@400;500;600;700&family=Cairo:wght@400;600;700;800&display=swap";
export const loadReservationFonts = () => {
  if (document.querySelector(`link[href="${FONTS_HREF}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = FONTS_HREF;
  document.head.appendChild(link);
};

// ── Dates ────────────────────────────────────────────────────────────────────
// Slot dates travel as "YYYY-MM-DD" and times as "HH:MM:SS" (local, no zone).
const pad = (n) => String(n).padStart(2, "0");
export const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const parseYmd = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const todayYmd = () => ymd(new Date());
export const monthStart = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
export const monthEnd = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

export const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;
};
export const formatRange = (start, end) => `${formatTime(start)} – ${formatTime(end)}`;
export const formatDateLong = (s) =>
  parseYmd(s).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
export const formatDateShort = (s) =>
  parseYmd(s).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
export const formatMonth = (d) => d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

export const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Every day shown in a month grid (Sunday-first, padded to full weeks).
export const monthGrid = (month) => {
  const first = monthStart(month);
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - first.getDay());
  const last = monthEnd(month);
  const days = [];
  for (let d = start; d <= last || d.getDay() !== 0; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    days.push({ key: ymd(d), day: d.getDate(), inMonth: d.getMonth() === first.getMonth() });
  }
  return days;
};

// Groups rows by a key function into { key: [rows] }.
export const groupBy = (rows, keyFn) =>
  rows.reduce((acc, row) => {
    (acc[keyFn(row)] = acc[keyFn(row)] || []).push(row);
    return acc;
  }, {});
